/** The record-panel selection (`?rec=<crm_list_entry id>`) — open / repoint / toggle-closed.
 *
 * A leaf module (no AG, no component) so this can be tested against the REAL implementation. It used
 * to live inline in `CrmListGrid` with a byte-for-byte replica in the test file kept "in lockstep" by
 * convention — which meant the §7.2 regression tests passed over a copy and would not have caught a
 * divergence in the shipped one. Moving it down here costs nothing and makes those tests real.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useSelectionParam } from '../../hooks/navigation/useUrlState';

export interface RecordSelection {
  /** The open entry id, or null. Parsed from the URL — the single source of truth. */
  recEntryId: number | null;
  /** Open / repoint / toggle-closed, depending on what is already open. Identity-stable. */
  openRecord: (entryId: number) => void;
  /** Close the panel. Identity-stable. */
  closeRecord: () => void;
}

/**
 * @param onOpen ran just before a NEW record opens (the caller closes its side docks here). Not
 *   called on a toggle-close, which is not an open.
 */
export function useRecordSelection(onOpen?: () => void): RecordSelection {
  const rec = useSelectionParam('rec');
  const recEntryId = rec.value != null && /^\d+$/.test(rec.value) ? parseInt(rec.value, 10) : null;
  // §7.2: track the currently-open entry id in a ref so the STABLE openRecord callback (below) never
  // reads a stale value. openRecord is delivered to name cells via AG grid `context`, and AG does
  // NOT re-render a cell when the context object changes — a cell rendered before the first open
  // would otherwise keep calling an openRecord closed over `recEntryId === null` and PUSH a new
  // history entry on every repoint (the stacking defect). An effect syncs the ref to URL-driven
  // open/close/deep-link (a render-time write trips react-hooks/refs and is unsafe under concurrent
  // render); the SYNCHRONOUS in-callback write in openRecord bridges two rapid clicks that both fire
  // before this effect re-runs (openRecord is only ever invoked from post-commit user events).
  const recEntryIdRef = useRef<number | null>(null);
  useEffect(() => { recEntryIdRef.current = recEntryId; }, [recEntryId]);
  // `onOpen` through a ref, not a dep: a caller passing an inline arrow would otherwise give
  // openRecord a new identity every render, which is exactly the churn §7.2 forbids.
  const onOpenRef = useRef(onOpen);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
  const recOpen = rec.open; // stable useCallback — depend on these (not the fresh rec object) so
  const recClose = rec.close; // openRecord/openDock/gridContext don't churn every render (review fix)
  const openRecord = useCallback((entryId: number) => {
    // §5.7(c): re-clicking the SAME name CLOSES the panel — the name is a toggle. A DIFFERENT name
    // still repoints under the §7.2 contract below, so a browse remains one Back-step; only the
    // same-id case is new. The synchronous ref write matters here too: two rapid clicks on one name
    // must open-then-close, not open twice.
    if (recEntryIdRef.current === entryId) {
      recEntryIdRef.current = null;
      recClose();
      return;
    }
    onOpenRef.current?.();
    // REPLACE when a record is already open (repoint = one Back-step for the whole browse); PUSH on
    // the first open. Read the ref (not a closed-over recEntryId) so the context-delivered callback
    // can't go stale; then update it synchronously so a second rapid click also replaces.
    recOpen(String(entryId), recEntryIdRef.current != null ? { replace: true } : undefined);
    recEntryIdRef.current = entryId;
  }, [recOpen, recClose]);
  return { recEntryId, openRecord, closeRecord: recClose };
}
